import { isAtOrPastPickup, isDeletedStatus, isFailed, isUpcomingStatus } from './guestRideUtils';

/** `none` is a ride that was never held for approval, which is every ride on a rooftop with it off. */
export type GuestRideApprovalState = 'none' | 'waiting' | 'approved' | 'denied';

/** The fields of a guest ride the approval UI reads. */
export interface ApprovalRide {
  id?: number;
  status?: string | null;
  approval_user_id?: number | null;
  approval_decision?: string | null;
  approval_decision_reason?: string | null;
  approval_decided_at?: string | null;
  approver?: { display_name?: string | null } | null;
  ridehails?: Array<{ id?: number; status?: string | null; external_id?: string | null }> | null;
}

export const approvalStateOf = (ride: ApprovalRide | null | undefined): GuestRideApprovalState => {
  if (!ride || ride.approval_user_id === null || ride.approval_user_id === undefined) return 'none';
  if (ride.approval_decision === 'approved') return 'approved';
  if (ride.approval_decision === 'denied') return 'denied';
  return 'waiting';
};

/** A held ride still in a status the rides service can decide, resend or book. */
const isOpen = (ride: ApprovalRide) => ride?.status === 'new' || ride?.status === 'failed';

export interface GuestRideActions {
  /** `retry-edit` opens the edit-then-call modal; `retry` re-calls an unchanged trip. */
  call: 'call' | 'retry' | 'retry-edit' | null;
  /** Cancel a booked ride with the provider. */
  cancel: boolean;
  /** Cancel a held ride that has no provider booking yet. */
  cancelRequest: boolean;
  edit: boolean;
  delete: boolean;
  revert: boolean;
  resend: boolean;
}

/**
 * What a dealer can do to a ride from the list. A ride held for approval can't be edited,
 * deleted or reverted: someone else approves the trip as it stands, and the server refuses
 * those writes. Rides that were never held keep exactly the actions they always had.
 */
export const guestRideActionsFor = (ride: ApprovalRide, canManage: boolean): GuestRideActions => {
  const status = ride?.status ?? '';
  const upcoming = isUpcomingStatus(status);
  const failed = isFailed(ride);
  const deleted = isDeletedStatus(status);
  // Only once the ride is booked with a provider (the ridehail has an external_id). Before that
  // there's no provider ride to cancel and the rides API rejects it.
  const cancel =
    canManage && !deleted && ride?.status !== 'new' && !isAtOrPastPickup(status) && !!ride?.ridehails?.[0]?.external_id;
  const approval = approvalStateOf(ride);

  if (approval === 'none') {
    return {
      call: canManage && (upcoming || failed) ? (failed ? 'retry-edit' : 'call') : null,
      cancel,
      cancelRequest: false,
      edit: canManage && !deleted && (ride?.status === 'new' || failed),
      delete: canManage && ride?.status === 'new',
      revert: canManage && deleted,
      resend: false,
    };
  }

  const none = { call: null, cancel, cancelRequest: false, edit: false, delete: false, revert: false, resend: false };
  if (approval === 'denied') return none;
  if (approval === 'waiting') {
    return { ...none, cancelRequest: canManage && isOpen(ride), resend: canManage && isOpen(ride) };
  }
  // Approved: a booking that failed or never started is re-called as is, with no second approval.
  return {
    ...none,
    call: canManage && (upcoming || failed) ? (failed ? 'retry' : 'call') : null,
    cancelRequest: canManage && isOpen(ride) && !cancel,
  };
};
